/**
 * Admin shell, built on the same structure as the user app's: a fixed sidebar
 * (logo, grouped nav with section labels, Collapse at the foot), a top bar
 * (environment badge, account menu with the theme selector), and a content
 * column. Mobile: the sidebar is a drawer over the page.
 *
 * What the user app's shell has that this one does not, on purpose: persona
 * switcher, streak, notifications bell, feedback button and search. They are
 * about a creator's own account; the console has no counterpart for them.
 */

import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ArrowSquareOut,
  Bell,
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretDown,
  Cpu,
  CreditCard,
  FileText,
  Funnel,
  SquaresFour as LayoutDashboard,
  List as Menu,
  ChatCircleDots as MessageSquare,
  Question,
  Scroll as ScrollText,
  ShieldCheck,
  SignOut,
  Target,
  Terminal,
  TrendUp,
  User,
  Users,
  UsersThree,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import {
  Badge,
  cn,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  ThemeSelector,
  Tooltip,
} from "@charisol-spark/ui";
import { getPlatformRole, hasRole } from "@/components/auth/AdminGuard";
import { useAuth } from "@/hooks/useAuth";
import { useUIStore } from "@/stores/uiStore";
import { config } from "@/config";

type Role = "platform_admin" | "platform_support" | "platform_analyst";

interface AdminNavItem {
  to: string;
  icon: Icon;
  label: string;
  /** Roles that can see this item */
  roles?: Role[];
}

interface AdminNavGroup {
  /** Small uppercase label above the group; the top group has none. */
  label?: string;
  items: AdminNavItem[];
}

// Paths are relative to this app's own root ("/"), not "/app/admin/*": the
// admin app has no "/app" of its own; everything it serves is the console.
const navGroups: AdminNavGroup[] = [
  {
    items: [{ to: "/", icon: LayoutDashboard, label: "Overview" }],
  },
  {
    label: "People",
    items: [
      {
        to: "/users",
        icon: Users,
        label: "Users",
        roles: ["platform_admin", "platform_support"],
      },
      {
        to: "/content",
        icon: FileText,
        label: "Content",
        roles: ["platform_admin", "platform_support"],
      },
      {
        to: "/explore",
        icon: TrendUp,
        label: "Explore",
        roles: ["platform_admin", "platform_analyst"],
      },
      {
        to: "/feedback",
        icon: MessageSquare,
        label: "Feedback",
        roles: ["platform_admin", "platform_support"],
      },
      {
        to: "/reports",
        icon: WarningCircle,
        label: "Reports",
        roles: ["platform_admin"],
      },
      {
        to: "/help-questions",
        icon: Question,
        label: "Help questions",
        roles: ["platform_admin", "platform_support"],
      },
    ],
  },
  {
    label: "Business",
    items: [
      {
        to: "/ai-usage",
        icon: Cpu,
        label: "Usage & Cost",
        roles: ["platform_admin", "platform_analyst"],
      },
      {
        to: "/onboarding",
        icon: Funnel,
        label: "Onboarding",
        roles: ["platform_admin", "platform_analyst"],
      },
      {
        to: "/acquisition",
        icon: Target,
        label: "Acquisition",
        roles: ["platform_admin", "platform_support", "platform_analyst"],
      },
      {
        to: "/billing",
        icon: CreditCard,
        label: "Billing",
        roles: ["platform_admin", "platform_analyst"],
      },
    ],
  },
  {
    label: "Platform",
    items: [
      {
        to: "/audit",
        icon: ScrollText,
        label: "Audit Logs",
        roles: ["platform_admin"],
      },
      {
        to: "/alerts",
        icon: Bell,
        label: "Alerts",
        roles: ["platform_admin"],
      },
      {
        to: "/logs",
        icon: Terminal,
        label: "CloudWatch Logs",
        roles: ["platform_admin"],
      },
      {
        to: "/internal-interviews",
        icon: UsersThree,
        label: "Interviews",
        roles: ["platform_admin", "platform_support"],
      },
    ],
  },
];

/** One rhythm for every row in the nav, same as the user app's sidebar. */
const NAV_ITEM_BASE =
  "flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-colors";

function isNavActive(to: string, pathname: string) {
  if (to === "/") return pathname === "/";
  return pathname.startsWith(to);
}

function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const mobileMenuOpen = useUIStore((s) => s.mobileMenuOpen);
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);
  const location = useLocation();

  const groups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.roles || hasRole(...item.roles)
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-50 flex h-screen flex-col",
        "border-r border-border/50 bg-card transition-all duration-300",
        "-translate-x-full md:translate-x-0",
        mobileMenuOpen && "translate-x-0",
        "w-56",
        collapsed ? "md:w-16" : "md:w-56"
      )}
      aria-label="Admin navigation"
    >
      <div className="flex h-16 items-center justify-between gap-2 px-4">
        <div className="flex min-w-0 items-center md:hidden">
          <img src="/full-logo.svg" alt="Spark" className="h-14" />
        </div>
        {/* The logo is a mark, not a control: collapsing lives on the toggle
            at the foot, which says what it does. */}
        <div className="hidden min-w-0 items-center md:flex">
          <img
            src="/full-logo.svg"
            alt="Spark"
            className={cn("h-14", collapsed ? "hidden" : "block")}
          />
          <img
            src="/sparkIcon.svg"
            alt="Spark"
            className={cn(
              "h-7 w-7 object-contain",
              collapsed ? "block" : "hidden"
            )}
          />
        </div>
        <Tooltip content="Close navigation" side="bottom" className="md:hidden">
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="rounded-lg p-2 transition-colors hover:bg-accent md:hidden"
            aria-label="Close navigation"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </Tooltip>
      </div>

      <nav className="no-scrollbar flex-1 overflow-y-auto px-2 py-2">
        {groups.map((group, gi) => (
          <div key={gi}>
            {gi > 0 && <div className="mt-4" />}
            {group.label && (
              <p
                className={cn(
                  "px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60",
                  collapsed && "md:hidden"
                )}
              >
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active = isNavActive(item.to, location.pathname);
                const NavIcon = item.icon;
                return (
                  <Tooltip
                    key={item.to}
                    disabled={!collapsed}
                    content={item.label}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        NAV_ITEM_BASE,
                        active
                          ? "bg-accent text-foreground"
                          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                        collapsed && "md:justify-center md:px-2"
                      )}
                      aria-label={item.label}
                    >
                      <NavIcon className="h-5 w-5 shrink-0" />
                      <span className={cn("block", collapsed && "md:hidden")}>
                        {item.label}
                      </span>
                    </NavLink>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="hidden border-t border-border/50 p-2 md:block">
        <Tooltip disabled={!collapsed} content="Expand sidebar">
          <button
            onClick={toggleSidebar}
            className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <CaretDoubleRight className="h-4 w-4" />
            ) : (
              <>
                <CaretDoubleLeft className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </Tooltip>
      </div>
    </aside>
  );
}

const MENU_ITEM =
  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-foreground transition-colors hover:bg-accent";

function Header() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleMobileMenu = useUIStore((s) => s.toggleMobileMenu);
  const { user, logout } = useAuth();
  const role = getPlatformRole();

  return (
    <header
      className={cn(
        "fixed right-0 top-0 z-40 flex h-16 items-center justify-between bg-background px-4 md:px-6",
        "transition-all duration-300",
        // A fade at the bottom edge into the page, as in the user app.
        "after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-8 after:bg-linear-to-b after:from-background after:to-transparent",
        "left-0",
        collapsed ? "md:left-16" : "md:left-56"
      )}
    >
      <div className="flex items-center gap-3">
        <Tooltip content="Open navigation" side="bottom" className="md:hidden">
          <button
            onClick={toggleMobileMenu}
            className="rounded-lg p-2 transition-colors hover:bg-accent md:hidden"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5 text-foreground/70" />
          </button>
        </Tooltip>

        {config.environmentBadge && (
          <Badge
            variant="warning"
            className="shrink-0 px-1.5 py-0 text-[10px] leading-4"
          >
            {config.environmentBadge}
          </Badge>
        )}
        {role && (
          <span className="hidden items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground sm:inline-flex">
            <ShieldCheck className="h-3.5 w-3.5" />
            {role.replace("platform_", "")}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {config.appUrl && (
          <a
            href={config.appUrl}
            className="mr-1 hidden items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:flex"
          >
            Spark app
            <ArrowSquareOut className="h-3.5 w-3.5" />
          </a>
        )}

        <div className="mx-1 h-5 w-px bg-border/50" />

        <Popover>
          <Tooltip content="Account" side="bottom">
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="User menu"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
                  <User className="h-4 w-4" />
                </div>
                <CaretDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" />
              </button>
            </PopoverTrigger>
          </Tooltip>

          <PopoverContent align="end" sideOffset={8} className="w-56 p-1.5">
            <div className="mb-1 px-2.5 py-2">
              <p className="truncate text-sm font-semibold">
                {user?.name || "Admin"}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {user?.email}
              </p>
              {role && (
                <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {role.replace("platform_", "")}
                </span>
              )}
            </div>

            <div className="my-1 border-t border-border/50" />

            {/* The same control, above the same menu, as the user app. */}
            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
              <span className="text-sm text-foreground">Theme</span>
              <ThemeSelector />
            </div>

            <div className="my-1 border-t border-border/50" />

            <PopoverClose asChild>
              <NavLink to="/settings/security" className={MENU_ITEM}>
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                Security
              </NavLink>
            </PopoverClose>

            <div className="my-1 border-t border-border/50" />

            <PopoverClose asChild>
              <button
                onClick={logout}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive/10"
              >
                <SignOut className="h-4 w-4" />
                Log out
              </button>
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}

export function AdminLayout() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const mobileMenuOpen = useUIStore((s) => s.mobileMenuOpen);
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);

  // The "set up an authenticator app" nudge that used to live here is gone:
  // AdminMfaGate, one layer up in the route tree, forces enrolment to
  // completion before this component or any admin page renders.

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <main
        className={cn(
          "pl-0 pt-16 transition-all duration-300",
          collapsed ? "md:pl-16" : "md:pl-56"
        )}
      >
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:px-8 md:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
